/** TanStack Query hooks for learner enrolments and per-course progress. */
import { useQuery } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import type { EnrolmentWithCourse, CourseProgress } from '@formency/shared';

// Re-export for convenience so consumers don't need a separate import
export type { EnrolmentWithCourse } from '@formency/shared';

// ─── Query key factory ─────────────────────────────────────────────────────

export const enrolmentKeys = {
  all: ['f', 'enrolments'] as const,
  me: () => [...enrolmentKeys.all, 'me'] as const,
  progress: (courseId: string) => [...enrolmentKeys.all, 'progress', courseId] as const,
};

// ─── Queries ───────────────────────────────────────────────────────────────

/** Fetch all enrolments for the current user (each includes the joined course object). */
export function useMyEnrolments(options?: { enabled?: boolean; staleTime?: number }) {
  return useQuery<EnrolmentWithCourse[]>({
    queryKey: enrolmentKeys.me(),
    queryFn: () => api.get<EnrolmentWithCourse[]>('/me/enrolments'),
    staleTime: 5 * 60_000,
    ...options,
  });
}

/** Fetch progress for a specific course enrolment. */
export function useCourseProgress(courseId: string | undefined) {
  return useQuery<CourseProgress>({
    queryKey: enrolmentKeys.progress(courseId!),
    queryFn: () => api.get<CourseProgress>(`/me/enrolments/${courseId}/progress`),
    enabled: !!courseId,
    staleTime: 30_000,
  });
}
